import { dayNumber } from './trend'
import type { ComparisonResult } from './api'
import type { HomeDisplayView } from './home'

/**
 * Drawing geometry for `/state`, the approved prototype's numbers (R72).
 *
 * Every function here turns a STORED value, a list index or a box size into a
 * pixel position, exactly as `brainspot-patterns-styled.html` does: the map's
 * 32/35 px insets, the charts' 31/12/12/26 px margins, the 330 px label rule
 * and the popover's placement. Nothing here compares a value with a threshold,
 * averages, smooths, or produces a number anyone reads as a result; a value is
 * only ever projected onto the axis clip the payload itself carries.
 */

export type Clip = readonly [number, number]

/** The state map's plotting box inside a `w` x `h` square (prototype `drawMap`). */
export function mapFrame(w: number, h: number, clip: Clip) {
  const padX = 32
  const padY = 35
  const pw = w - 2 * padX
  const ph = h - 2 * padY
  const span = clip[1] - clip[0]
  return {
    padX, padY, pw, ph,
    x: (v: number) => padX + ((v - clip[0]) / span) * pw,
    y: (v: number) => h - padY - ((v - clip[0]) / span) * ph,
    /** The "usual" ring: the payload's dead zone, on the same scale. */
    ring: (dz: number) => (dz / span) * Math.min(pw, ph),
    /** A producer-supplied ellipse; project its axes without estimating a range. */
    ellipse: ({ cx, cy, rx, ry }: { cx: number; cy: number; rx: number; ry: number }) => ({
      cx: padX + ((cx - clip[0]) / span) * pw,
      cy: h - padY - ((cy - clip[0]) / span) * ph,
      rx: (rx / span) * pw,
      ry: (ry / span) * ph,
    }),
    right: w - padX,
    bottom: h - padY,
    centre: w / 2,
    /** "Less activated ↓", 9 px above the square's foot. */
    footLabelY: h - 9,
    /** The corner pills: 8 px in from the plotting box. */
    cornerInset: padX + 8,
    cornerTop: padY + 8,
    cornerFoot: h - padY - 27,
    /** The "no map position" banner across the lower half. */
    banner: { x: padX + 10, y: h - padY - 59, width: pw - 20, textY: h - padY - 42 },
  }
}

/** Is a stored pair drawable on the map: both present and inside the payload's clip. */
export function onMap(a: number | null, b: number | null, clip: Clip): boolean {
  return a !== null && b !== null && a >= clip[0] && a <= clip[1] && b >= clip[0] && b <= clip[1]
}

/** Both owned positions must be present and inside the supplied clip before reserving map space. */
export function comparisonMapIsDrawable(result: ComparisonResult, display: HomeDisplayView | null): boolean {
  const before = result.points?.recordings?.find((point) => point.recording_id === result.before_recording_id)?.state_map
  const after = result.points?.recordings?.find((point) => point.recording_id === result.after_recording_id)?.state_map
  return display !== null && before !== undefined && after !== undefined
    && typeof before.activation === 'number' && typeof before.balance === 'number'
    && typeof after.activation === 'number' && typeof after.balance === 'number'
    && onMap(before.activation, before.balance, display.axisClip)
    && onMap(after.activation, after.balance, display.axisClip)
}

/** One timeline's drawing box (prototype `drawChart`): `n` dates, evenly spaced. */
export function chartFrame(w: number, h: number, n: number, clip: Clip) {
  const l = 31
  const r = 12
  const t = 12
  const b = 26
  const inner = w - l - r
  const span = clip[1] - clip[0]
  const x = (i: number) => (n > 1 ? l + (i / (n - 1)) * inner : l + inner / 2)
  const step = n > 1 ? inner / (n - 1) : inner
  const y = (v: number) => t + ((clip[1] - Math.max(clip[0], Math.min(clip[1], v))) / span) * (h - t - b)
  return {
    l, r, t, b,
    x,
    y,
    /** Tick numbers sit 8 px left of the plot, 4 px below their line. */
    tickX: l - 8,
    tickY: (tick: number) => y(tick) + 4,
    /** The prototype's "×" for a missing value among stored ones, on the zero line of its date. */
    gapY: y(0) + 4,
    top: t,
    bottom: h - b,
    right: w - r,
    labelY: h - 7,
    /** The column a date's transparent pick button covers. */
    pick: (i: number) => {
      const left = Math.max(l - 12, x(i) - step / 2)
      return { left, width: Math.min(w - r, x(i) + step / 2) - left }
    },
  }
}

/** The prototype's three gridlines (-2, 0, +2 on a ±3 clip): one in from each clip edge, and zero. */
export function chartTicks(clip: Clip): number[] {
  return [clip[0] + 1, 0, clip[1] - 1]
}

/** A date number under a chart: every one when there is room, else every other (prototype: 330 px). */
export function showsDayLabel(width: number, index: number): boolean {
  return width > 330 || index % 2 === 0
}

/** Two recording dates one calendar day apart: only those are joined by a line. */
export function nextCalendarDay(earlier: string, later: string): boolean {
  const a = dayNumber(earlier)
  const b = dayNumber(later)
  return a !== null && b !== null && b - a === 1
}

/** Previous / next among the loaded dates; null past either end. */
export function stepTo(index: number, delta: -1 | 1, count: number): number | null {
  const next = index + delta
  return next < 0 || next >= count ? null : next
}

/** Where a floating help sits: centred on its trigger, kept 12 px inside the viewport, flipped when it would not fit. */
export function tipPlacement(
  trigger: { top: number; bottom: number; left: number; width: number },
  bubble: { width: number; height: number },
  viewport: { width: number; height: number },
  prefer: 'top' | 'bottom',
): { top: number; left: number; side: 'top' | 'bottom' } {
  const gap = 8
  const margin = 12
  const above = trigger.top - gap - bubble.height
  const below = trigger.bottom + gap
  const side = prefer === 'top'
    ? (above < margin ? 'bottom' : 'top')
    : (below + bubble.height > viewport.height - margin && above >= margin ? 'top' : 'bottom')
  const centred = trigger.left + trigger.width / 2 - bubble.width / 2
  const left = Math.max(margin, Math.min(viewport.width - margin - bubble.width, centred))
  return { top: side === 'top' ? above : below, left, side }
}

/**
 * EXP-003: clock-to-pixel geometry only. It never selects the daily reading or
 * reads a score. Close labels occupy separate vertical lanes, leaving at least
 * 24 px between mark centres even for two readings at the same wall clock.
 */
export function dayAxisLayout(readings: readonly { id: string; time: string | null | undefined }[], width: number) {
  const clockMinutes = (time: string | null | undefined): number | null => {
    if (time == null) return null
    const match = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.(\d+))?)?$/.exec(time)
    return match === null ? null : Number(match[1]) * 60 + Number(match[2]) + Number(match[3] ?? 0) / 60
  }
  const timed = readings.flatMap((reading) => {
    const at = clockMinutes(reading.time)
    return at === null ? [] : [{ id: reading.id, at }]
  }).sort((a, b) => a.at - b.at)
  const start = Math.floor(Math.min(360, ...timed.map((r) => r.at)) / 60) * 60
  const end = Math.ceil(Math.max(1320, ...timed.map((r) => r.at)) / 60) * 60
  const inset = 18
  const inner = Math.max(1, width - inset * 2)
  const x = (minute: number) => inset + (minute - start) / (end - start) * inner
  const labelWidth = Math.min(170, inner)
  // The label box reserves three 11px/1.3 lines (rounded up). An adjacent
  // lane's 14px marker must end above this box, with an 8px clear gap.
  const labelHeight = 44
  const labelOffset = 55
  const markerRadius = 7
  const labelMarkerGap = 8
  const lanePitch = labelOffset + markerRadius + labelMarkerGap
  const lanes: { markerX: number; labelRight: number }[] = []
  const positioned = timed.map((reading) => {
    const at = x(reading.at)
    // Collision checks use the box that will actually be painted. Near either
    // edge a label is clamped away from its marker, so marker distance alone
    // can put two overlapping labels on one lane.
    const labelLeft = Math.max(inset, Math.min(width - inset - labelWidth, at - labelWidth / 2))
    let lane = lanes.findIndex((last) => at - last.markerX >= 24 && labelLeft - last.labelRight >= 8)
    if (lane === -1) lane = lanes.length
    lanes[lane] = { markerX: at, labelRight: labelLeft + labelWidth }
    return { ...reading, x: at, labelLeft, lane }
  })
  const height = 112 + Math.max(0, lanes.length - 1) * lanePitch
  const axisY = height - 28
  const clock = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(Math.round(minutes % 60)).padStart(2, '0')}`
  return {
    height,
    axisY,
    start: clock(start),
    end: clock(end),
    marks: positioned.map((mark) => ({
      id: mark.id,
      x: mark.x,
      y: axisY - mark.lane * lanePitch,
      labelLeft: mark.labelLeft,
      labelWidth,
      labelHeight,
      labelY: axisY - mark.lane * lanePitch - labelOffset,
    })),
    ticks: Array.from({ length: 5 }, (_, index) => {
      const at = start + (end - start) * index / 4
      return { x: x(at), text: clock(at) }
    }),
  }
}
